import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  GRANT_CLOSED_MODES,
  GRANT_SORTS,
  GRANT_SUPPORT_FILTERS,
  type GrantClosedMode,
  type GrantSort,
  type GrantSupportFilter,
} from '@zoxo/shared';

import { PageContainer } from '../components/layout/page-container';
import { writeListCookie } from '../features/grants/list-state';
import { GrantList } from '../features/grants/grant-list';
import { grantListDeps, loadGrantList } from '../features/grants/list-loader';
import { TYPE_PATH } from '../features/grants/type-routes';
import type { GrantFilters } from '../features/grants/use-grants';
import { pageHead, PUBLISHER_LD } from '../lib/seo';
import { canonical, SITE_DESCRIPTION, SITE_NAME } from '../lib/site';

/**
 * 홈 — 정부지원사업 목록.
 *
 * 메인 필라가 루트에 온다. 필터·정렬은 **URL 에 산다**(`?region=&sort=`) —
 * 공유 가능하고 뒤로가기가 동작해야 한다.
 *
 * 히어로에 그라디언트나 큰 숫자를 두지 않았다. 이 화면의 유일한 일은 "나에게
 * 해당하는 지원금을 찾는 것"이고, 그 일을 가장 잘 돕는 것은 목록을 위로
 * 끌어올리는 것이다. 설명은 한 줄이면 된다.
 */
export const Route = createFileRoute('/news/')({
  validateSearch: (search): GrantFilters => {
    const sort = search.sort;
    return {
      region: typeof search.region === 'string' && search.region ? search.region : undefined,
      field: typeof search.field === 'string' && search.field ? search.field : undefined,
      support:
        typeof search.support === 'string' &&
        (GRANT_SUPPORT_FILTERS as readonly string[]).includes(search.support)
          ? (search.support as GrantSupportFilter)
          : undefined,
      closed:
        typeof search.closed === 'string' &&
        (GRANT_CLOSED_MODES as readonly string[]).includes(search.closed) &&
        search.closed !== 'exclude'
          ? (search.closed as GrantClosedMode)
          : undefined,
      sort:
        typeof sort === 'string' && (GRANT_SORTS as readonly string[]).includes(sort)
          ? (sort as GrantSort)
          : undefined,
      q: typeof search.q === 'string' && search.q.trim() ? search.q.trim().slice(0, 60) : undefined,
    };
  },
  loaderDeps: ({ search }) => grantListDeps(search),
  loader: ({ context, deps }) => loadGrantList(context, deps),
  head: () =>
    pageHead({
      title: '정부지원사업 공고 — 기업마당 · K-Startup · 중기부',
      description: SITE_DESCRIPTION,
      url: canonical('/news'),
      type: 'website',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          url: canonical('/'),
          description: SITE_DESCRIPTION,
          publisher: { '@type': 'Organization', name: SITE_NAME, url: canonical('/') },
        },
        { '@context': 'https://schema.org', ...PUBLISHER_LD },
      ],
    }),
  component: HomePage,
});

function HomePage() {
  const search = Route.useLoaderDeps();
  // URL 이 이기고, 없으면 쿠키(마지막 선택), 없으면 기본 — loader 가 정했다(서버와 같은 값).
  const { state, now } = Route.useLoaderData();
  const filters: GrantFilters = { ...search, ...state };
  const navigate = useNavigate({ from: '/news/' });
  // 옛 링크 /news?support=grant 는 기둥 경로(/news/grants)로 — 같은 목록을 두 주소로 내지 않는다.
  useEffect(() => {
    if (filters.support) {
      const { support, ...rest } = filters;
      void navigate({ to: TYPE_PATH[support], search: rest as never, replace: true });
    }
  }, [filters, navigate]);

  return (
    <PageContainer allowOverflow>
      <h1 className="sr-only">정부지원사업</h1>

      <div>
        <GrantList
          filters={filters}
          now={new Date(now)}
          onChange={(next) => {
            writeListCookie(next);
            void navigate({
              search: {
                ...(next.region ? { region: next.region } : {}),
                ...(next.field ? { field: next.field } : {}),
                ...(next.support ? { support: next.support } : {}),
                ...(next.closed && next.closed !== 'exclude' ? { closed: next.closed } : {}),
                ...(next.sort && next.sort !== 'latest' ? { sort: next.sort } : {}),
                ...(next.q ? { q: next.q } : {}),
              },
              replace: true,
            });
          }}
        />
      </div>
    </PageContainer>
  );
}
